// @flow

import { isLocalLatamTenantCode } from '@tectonics/commons/tenant';
import { shouldIgnoreAddress, shouldIgnorePhoneNumber } from '@tectonics/prs/ui';
import {
  getSafeCreditCardPaymentOption,
  getSafeDebitCardPaymentOption,
} from '@tectonics/prs/incoming-payment/payment-option';
import { type PaymentSpecGenerator } from '@tectonics/prs/payment-spec/generator';

import {
  isLocalPayment,
  getSafePaymentTypeCode,
  getSafeUi,
  needsCaptcha,
  shouldHideCustomerEmailField,
} from '@tectonics/prs/incoming-payment';
import { getSafePaymentOptionsV2 } from '@tectonics/prs/incoming-payment/get-payment-options';
import { getGooglePayClientInstance } from '@tectonics/prs/google-pay';

const paymentSpecGenerator: PaymentSpecGenerator = {

  features: ({ incomingPayment, localTenancy }) => {
    const features = [
      { name: 'display_merchant_logo', traits: [] },
      { name: 'display_receipt_original_amount', traits: [] },
      ...(isLocalPayment(incomingPayment) ? [] : [{ name: 'display_receipt_fx_rate', traits: [] }]),
      { name: 'display_receipt_final_amount', traits: [] },
    ];

    if (incomingPayment.status === 'OP') {
      const ui = getSafeUi(incomingPayment);

      return [
        ...features,
        { name: 'field_customer_name', traits: [] },
        ...(shouldIgnorePhoneNumber(ui) ? [] : [{ name: 'field_mexican_phone_number', traits: [] }]),
        { name: 'field_accept_terms', traits: [] },
        ...(needsCaptcha(incomingPayment) ? [{ name: 'field_captcha_token', traits: [] }] : []),
        ...(isLocalLatamTenantCode(localTenancy) ? [{ name: 'hide_amex_card_number', traits: [] }] : []),
        { name: 'display_back_button', traits: [] },
      ];
    }

    return features;
  },

  creditcard: ({ incomingPayment }) => {
    const features = [
      { name: 'display_selected_instalment', traits: [] },
    ];

    if (incomingPayment.status === 'OP') {
      const paymentOptions = getSafePaymentOptionsV2(incomingPayment);
      const brands = getSafeCreditCardPaymentOption(paymentOptions);

      const ui = getSafeUi(incomingPayment);

      return [
        ...features,
        ...(shouldHideCustomerEmailField(incomingPayment) ? [] : [{ name: 'field_customer_email', traits: [] }]),

        { name: 'field_card_holder_name', traits: [] },
        { name: 'field_card_number', traits: [] },
        { name: 'field_card_expiry_date', traits: [] },
        { name: 'field_card_security_code', traits: [] },
        { name: 'field_card_instalments', traits: [] },
        { name: 'display_card_accepts_brand', traits: brands },
        { name: 'display_alternative_card_accepts_brand', traits: brands },
        ...(shouldIgnoreAddress(ui) ? [] : [
          { name: 'field_mexican_zipcode', traits: [] },
          { name: 'field_mexican_state', traits: [] },
          { name: 'field_mexican_city', traits: [] },
          { name: 'field_mexican_address', traits: [] },
          { name: 'field_mexican_street_number', traits: [] },
          { name: 'field_mexican_street_complement', traits: [] },
        ]),
      ];
    }

    return features;
  },

  debitcard: ({ incomingPayment }) => {
    if (incomingPayment.status === 'OP') {
      const paymentOptions = getSafePaymentOptionsV2(incomingPayment);
      const brands = getSafeDebitCardPaymentOption(paymentOptions);
      const ui = getSafeUi(incomingPayment);

      return [
        ...(shouldHideCustomerEmailField(incomingPayment) ? [] : [{ name: 'field_customer_email', traits: [] }]),

        { name: 'field_card_holder_name', traits: [] },
        { name: 'field_card_number', traits: [] },
        { name: 'field_card_expiry_date', traits: [] },
        { name: 'field_card_security_code', traits: [] },
        { name: 'display_debit_card_accepts_brand', traits: brands },

        ...(shouldIgnoreAddress(ui) ? [] : [
          { name: 'field_mexican_zipcode', traits: [] },
          { name: 'field_mexican_state', traits: [] },
          { name: 'field_mexican_city', traits: [] },
          { name: 'field_mexican_address', traits: [] },
          { name: 'field_mexican_street_number', traits: [] },
          { name: 'field_mexican_street_complement', traits: [] },
        ]),
      ];
    }

    return [];
  },

  oxxo: ({ incomingPayment }) => {
    if (incomingPayment.status === 'OP') {
      return [
        ...(shouldHideCustomerEmailField(incomingPayment) ? [] : [{ name: 'field_customer_email', traits: [] }]),
      ];
    }

    if (incomingPayment.status === 'PE') {
      return [
        { name: 'action_oxxo_save_pdf' },
        { name: 'display_oxxo_success_message' },
        { name: 'display_oxxo_number' },
        { name: 'display_oxxo_instructions' },
        { name: 'display_oxxo_payment_locations' },
        { name: 'display_oxxo_ticket_info' },
        { name: 'display_oxxo_total_amount' },
        { name: 'display_oxxo_duedate' },
        { name: 'display_oxxo_barcode' },
        { name: 'display_oxxo_tips' },
      ];
    }

    return [];
  },

  oxxopay: ({ incomingPayment }) => {
    if (incomingPayment.status === 'OP') {
      return [
        ...(shouldHideCustomerEmailField(incomingPayment) ? [] : [{ name: 'field_customer_email', traits: [] }]),
      ];
    }
    return [];
  },

  spei: ({ incomingPayment, lookAndFeelCode }) => {
    if (incomingPayment.status === 'OP') {
      return [
        ...(shouldIgnoreCustomerEmailForSpei() ? [] : [{ name: 'field_customer_email', traits: [] }]),
      ];
    }

    if (incomingPayment.status === 'PE') {
      return [
        { name: 'display_spei_success_message' },
        { name: 'display_spei_mail_warning' },
        { name: 'display_spei_total_amount' },
        { name: 'display_spei_due_date' },
        { name: 'display_spei_reference_number' },
        { name: 'display_spei_destination_clabe_number' },
        { name: 'display_spei_destination_bank' },
        { name: 'display_spei_destination_beneficiary' },
        { name: 'action_spei_save_pdf' },
        { name: 'display_spei_instructions' },
        { name: 'display_spei_tips' },
      ];
    }

    function shouldIgnoreCustomerEmailForSpei(): boolean {
      return lookAndFeelCode === 'uber' || shouldHideCustomerEmailField(incomingPayment);
    }
    return [];
  },

  cash: ({ incomingPayment, lookAndFeelCode }) => {
    return [
      ...(shouldIgnoreCustomerEmailForCashPayment() ? [] : [{ name: 'field_customer_email', traits: [] }]),

      { name: 'display_cash_total_value', traits: [] },
      { name: 'display_cash_due_date', traits: [] },
      { name: 'display_cash_barcode', traits: [] },
      { name: 'display_cash_number', traits: [] },
      { name: 'action_cash_download', traits: [] },
      { name: 'action_cash_save_pdf', traits: [] },
      { name: 'display_cash_email_warning', traits: [] },
      { name: 'display_cash_instructions', traits: [] },
      { name: 'display_cash_tips', traits: [] },
      { name: 'display_paynet_payment_locations', traits: [] },
      { name: 'display_cash_puntos', traits: [] },
      { name: 'display_cash_total_amount', traits: [] },
      { name: 'display_cash_duedate', traits: [] },
    ];

    function shouldIgnoreCustomerEmailForCashPayment(): boolean {
      return isUberOxxopay() || shouldHideCustomerEmailField(incomingPayment);
    }

    function isUberOxxopay(): boolean {
      return lookAndFeelCode === 'uber' && getSafePaymentTypeCode(incomingPayment) === 'oxxopay';
    }
  },

  ewallet: ({ incomingPayment }) => {
    switch(incomingPayment.status) {
      case 'OP': {
        return [
          ...(shouldHideCustomerEmailField(incomingPayment) ? [] : [{ name: 'field_customer_email', traits: [] }]),
          { name: 'display_ewallet_accepts_brand', traits: ['mercadopago'] },
        ];
      }

      default:
        return [];
    }
  },

  google: ({ incomingPayment }) => {
    if (!getGooglePayClientInstance()) {
      return undefined;
    }

    if (incomingPayment.status === 'OP') {
      return [
        { name: 'field_card_instalments', traits: [] },
        { name: 'display_selected_instalment' },
      ];
    }

    return [];
  },

  externalpayprovider: ({ incomingPayment }) => {
    if (incomingPayment.status === 'OP') {
      const ui = getSafeUi(incomingPayment);
      return [
        { name: 'display_aplazo_externalpayprovider_instructions', traits: []},
        ...(shouldIgnoreAddress(ui) ? [] : [
          {name: 'field_mexican_zipcode', traits: []},
          {name: 'field_mexican_state', traits: []},
          {name: 'field_mexican_city', traits: []},
          {name: 'field_mexican_address', traits: []},
          {name: 'field_mexican_street_number', traits: []},
        ]),
      ];
    }
    return [];
  },
};

export default paymentSpecGenerator;
